import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DropdownList } from 'react-widgets';
import CustomModal from '../../../Modal';
import FormGroup from '../../../FormGroup';
import { I18nFormatter } from 'utils/I18n/I18n';
import CurrencyActions from '../../../../actions/CurrencyActions';
import { useI18n } from 'utils/I18n/context';

export const ProfileAccountSettings = ({ currency, balanceAmount, currenciesList, rates, convertCurrentCurrency }) => {
    const { t } = useI18n();
    const [state, setState] = useState({
        prevCurrency: currency,
        currency,
        balanceAmount,
        currenciesList,
        rates,
        modalIsVisible: false,
        loading: false,
    });

    useEffect(() => {
        CurrencyActions.getCurrentCurrencyRatios();
    }, []);

    useEffect(() => {
        setState((prev) => ({ ...prev, balanceAmount, currency, currenciesList, rates }));
    }, [balanceAmount, currency, currenciesList, rates]);

    const showModal = useCallback(() => setState((prev) => ({ ...prev, modalIsVisible: true })), []);

    const closeModal = useCallback(() => setState((prev) => ({ ...prev, modalIsVisible: false, currency: prev.prevCurrency })), []);

    const handleCurrencyChange = useCallback(
        (item) => {
            const next = item && item.title ? item.title : '';
            setState((prev) => ({ ...prev, prevCurrency: prev.currency, currency: next }));
            showModal();
        },
        [showModal]
    );

    const getConvertRatioValue = useCallback(() => {
        if (state.rates?.[state.prevCurrency]?.[state.currency]?.bid) {
            return parseFloat(state.rates[state.prevCurrency][state.currency].bid).toFixed(6);
        }
        return '0';
    }, [state.rates, state.prevCurrency, state.currency]);

    const getBalanceAfterConverting = useCallback(() => {
        const ratio = parseFloat(getConvertRatioValue());
        return (parseFloat(state.balanceAmount || 0) * ratio).toFixed(2);
    }, [state.balanceAmount, getConvertRatioValue]);

    const onConvert = useCallback(() => {
        setState((prev) => ({ ...prev, loading: true }));
        convertCurrentCurrency(state, () => setState((prev) => ({ ...prev, loading: false, modalIsVisible: false })));
    }, [state, convertCurrentCurrency]);

    const staticNames = t.profilePage?.accountSettings || {};

    const convertRatioValue = getConvertRatioValue();
    const balanceAfterConverting = getBalanceAfterConverting();

    return (
        <React.Fragment>
            <CustomModal visible={state.modalIsVisible} onClose={closeModal} onConfirm={onConvert} loading={state.loading}>
                <div className="form-horizontal">
                    <FormGroup>
                        <div className="col-sm-8">{staticNames.currentBalance || 'Current Balance'}:</div>
                        {state.prevCurrency && <div className="col-sm-4 text-right">{I18nFormatter.currency(state.balanceAmount, state.prevCurrency)}</div>}
                    </FormGroup>
                    <FormGroup>
                        <div className="col-sm-8">{staticNames.buyCurrency || 'Buy Currency'}:</div>
                        <div className="col-sm-4 text-right">{state.currency}</div>
                    </FormGroup>
                    <FormGroup>
                        <div className="col-sm-8">{staticNames.convertRatio || 'Convert Ratio'} {state.prevCurrency}/{state.currency}</div>
                        <div className="col-sm-4 text-right">{I18nFormatter.currency(convertRatioValue, state.currency)}</div>
                    </FormGroup>
                    <FormGroup>
                        <div className="col-sm-8">{staticNames.nextBalance || 'Next Balance'}:</div>
                        <div className="col-sm-4 text-right">
                            <span className="helvetica">~</span>
                            {I18nFormatter.currency(balanceAfterConverting, state.currency)}
                        </div>
                    </FormGroup>
                </div>
            </CustomModal>

            <FormGroup label={`${staticNames.currency || 'Currency'}:`} className={{ 'currency-form': true }}>
                <DropdownList
                    className="form-control"
                    dataKey="title"
                    textField="title"
                    data={state.currenciesList}
                    value={state.currenciesList.find(c => c.title === state.currency) || null}
                    onChange={handleCurrencyChange}
                />
            </FormGroup>
        </React.Fragment>
    );
};

ProfileAccountSettings.propTypes = {
    currency: PropTypes.string,
    balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currenciesList: PropTypes.array,
    rates: PropTypes.object,
    convertCurrentCurrency: PropTypes.func.isRequired,
};

ProfileAccountSettings.defaultProps = {
    currency: '',
    balanceAmount: '',
    currenciesList: [],
    rates: {},
};